import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Switch,
  IconButton,
  Tooltip,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { adminAPI } from '../../utils/api';

const ViewerAssignmentCreate = () => {
  const [viewers, setViewers] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [selectedViewer, setSelectedViewer] = useState('');
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [candidateAccessLevels, setCandidateAccessLevels] = useState({}); // Map candidate_id -> full_access
  const [assignedCandidates, setAssignedCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedViewer) {
      loadAssignedCandidates(selectedViewer);
    } else {
      setAssignedCandidates([]);
    }
  }, [selectedViewer]);

  const loadData = async () => {
    setLoading(true);
    setError('');

    try {
      const [viewersData, candidatesData] = await Promise.all([
        adminAPI.getViewers(),
        adminAPI.getCandidates(),
      ]);

      setViewers(viewersData.viewers);
      setCandidates(candidatesData.candidates);
    } catch (err) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadAssignedCandidates = async (viewerId) => {
    try {
      const data = await adminAPI.getViewerAssignments(viewerId);
      setAssignedCandidates(data.candidates);
    } catch (err) {
      console.error('Failed to load assigned candidates:', err);
      setAssignedCandidates([]);
    }
  };

  const handleCandidateToggle = (candidateId) => {
    setSelectedCandidates((prev) => {
      const newSelection = prev.includes(candidateId)
        ? prev.filter((id) => id !== candidateId)
        : [...prev, candidateId];

      // Initialize access level for newly selected candidates (default: trial)
      if (!prev.includes(candidateId)) {
        setCandidateAccessLevels((prevLevels) => ({
          ...prevLevels,
          [candidateId]: false, // Default to trial (restricted)
        }));
      }

      return newSelection;
    });
  };

  const handleAccessLevelToggle = (candidateId) => {
    setCandidateAccessLevels((prev) => ({
      ...prev,
      [candidateId]: !prev[candidateId],
    }));
  };

  const handleToggleAssignedAccess = async (assignmentId, currentAccess) => {
    setSubmitting(true);
    setError('');

    try {
      await adminAPI.updateViewerAssignmentAccess(assignmentId, !currentAccess);
      setSuccess('Access level updated successfully!');
      await loadAssignedCandidates(selectedViewer);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to update access level');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedViewer) {
      setError('Please select a viewer');
      return;
    }

    if (selectedCandidates.length === 0) {
      setError('Please select at least one candidate');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      // Create assignments array with access levels
      const assignments = selectedCandidates.map((candidateId) => ({
        candidate_id: candidateId,
        full_access: candidateAccessLevels[candidateId] || false,
      }));

      await adminAPI.createViewerAssignmentsBulk(selectedViewer, assignments);
      setSuccess('Candidates assigned successfully!');
      setSelectedCandidates([]);
      setCandidateAccessLevels({});

      // Reload assigned candidates
      await loadAssignedCandidates(selectedViewer);
    } catch (err) {
      setError(err.message || 'Failed to assign candidates');
    } finally {
      setSubmitting(false);
    }
  };

  const isAlreadyAssigned = (candidateId) => {
    return assignedCandidates.some((c) => c.id === candidateId);
  };

  const getAssignedCandidate = (candidateId) => {
    return assignedCandidates.find((c) => c.id === candidateId);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Assign Candidates to Viewer
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Select Viewer</InputLabel>
          <Select
            value={selectedViewer}
            onChange={(e) => setSelectedViewer(e.target.value)}
            label="Select Viewer"
            required
          >
            {viewers.map((viewer) => (
              <MenuItem key={viewer.id} value={viewer.id}>
                {viewer.email}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedViewer && (
          <>
            {assignedCandidates.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Currently Assigned Candidates:
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                  {assignedCandidates.map((candidate) => (
                    <Box
                      key={candidate.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 1,
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                      }}
                    >
                      <Typography sx={{ flex: 1 }}>{candidate.email}</Typography>
                      <Chip
                        icon={candidate.full_access ? <LockOpenIcon /> : <LockIcon />}
                        label={candidate.full_access ? 'Full Access' : 'Trial'}
                        color={candidate.full_access ? 'success' : 'warning'}
                        size="small"
                      />
                      <Tooltip title={candidate.full_access ? 'Downgrade to Trial' : 'Upgrade to Full Access'}>
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleToggleAssignedAccess(candidate.assignment_id, candidate.full_access)
                          }
                          disabled={submitting}
                        >
                          {candidate.full_access ? <LockIcon /> : <LockOpenIcon />}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ))}
                </Box>
                <Divider sx={{ mb: 2 }} />
              </Box>
            )}

            <Typography variant="subtitle2" gutterBottom>
              Select Candidates to Assign:
            </Typography>

            {candidates.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No candidates available.
              </Typography>
            ) : (
              <FormGroup>
                {candidates.map((candidate) => {
                  const alreadyAssigned = isAlreadyAssigned(candidate.id);
                  const isSelected = selectedCandidates.includes(candidate.id);
                  const hasFullAccess = candidateAccessLevels[candidate.id] || false;

                  return (
                    <Box
                      key={candidate.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        p: 1,
                        borderRadius: 1,
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={alreadyAssigned || isSelected}
                            onChange={() => handleCandidateToggle(candidate.id)}
                            disabled={alreadyAssigned}
                          />
                        }
                        label={candidate.email}
                        sx={{ flex: 1 }}
                      />
                      {alreadyAssigned && (
                        <Chip label="Assigned" size="small" color="success" />
                      )}
                      {isSelected && !alreadyAssigned && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="caption">Access Level:</Typography>
                          <Chip
                            icon={hasFullAccess ? <LockOpenIcon /> : <LockIcon />}
                            label={hasFullAccess ? 'Full' : 'Trial'}
                            color={hasFullAccess ? 'success' : 'warning'}
                            size="small"
                          />
                          <Switch
                            checked={hasFullAccess}
                            onChange={() => handleAccessLevelToggle(candidate.id)}
                            size="small"
                          />
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </FormGroup>
            )}

            <Button
              type="submit"
              variant="contained"
              disabled={submitting || selectedCandidates.length === 0}
              sx={{ mt: 3 }}
              startIcon={submitting && <CircularProgress size={20} />}
            >
              {submitting ? 'Assigning...' : 'Assign Selected Candidates'}
            </Button>
          </>
        )}
      </Box>
    </Paper>
  );
};

export default ViewerAssignmentCreate;
